import { formatCents } from '@auctisync/shared';
import { type BidRoute, forgetBidRoute, getBidRoute } from './bid-route.js';
import { type AuctisyncSupabase, DEFAULT_REQUEST_TIMEOUT_MS, withTimeout } from './client.js';

/**
 * Bid origin tag. The `place_bid` DB column is unconstrained `text` (a
 * convention, not enforced) — see the Run Brief D-place_bid / the optional
 * CHECK-constraint follow-up.
 */
export type BidSource = 'web' | 'ios' | 'android' | 'clerk' | 'staff';

export interface PlaceBidInput {
  lotId: string;
  amountCents: number;
  /** The bidder's secret maximum (proxy ceiling). Omit for a plain bid — defaults to `amountCents`. */
  maxAmountCents?: number;
  /** Persisted-per-attempt UUID so a cellular retry is idempotent. */
  idempotencyKey: string;
  /** Origin tag; defaults to `'web'`. */
  source?: BidSource;
}

/**
 * The `place_bid` RPC return shape (its SQL `Returns` is `Json`). Fields present
 * by outcome; `success` is always set.
 */
export interface BidResult {
  success: boolean;
  reason?: string;
  status?: string;
  current_bid_cents?: number;
  next_min_cents?: number;
  triggered_extension?: boolean;
  ends_at?: string;
  bid_id?: string;
  required_cents?: number;
  /** Set on `ceiling_not_raised`: the maximum the caller already holds. */
  current_ceiling_cents?: number;
  /** The same attempt, answered again: the bid was placed by an earlier try with this key. */
  idempotent_replay?: boolean;
  message?: string;
}

/** How a bid reaches the engine: the fetch to use (the client's timeout and connection monitor). */
export interface BidTransport {
  fetch?: typeof fetch;
}

/**
 * THE ONLY sanctioned way to place a bid. Every bid path — web, native, clerk,
 * proxy, widget — goes through this funnel; nothing writes the append-only
 * `bids` ledger directly. Enforced in CI by `scripts/check-bid-contract.mjs`,
 * which also forbids calling `place_bid` outside this package.
 *
 * ONE BOOK PER SALE (2026-10-07). The database says where a lot's bids go
 * (`bid-route.ts`): the auction engine, or the `place_bid` ledger. The bid
 * goes there and nowhere else. An unreachable engine is NOT a reason to try
 * `place_bid` — that would be a second book for the same lot — so the bidder
 * gets the same "we could not confirm this bid" answer a dropped connection
 * always gave, the key is kept, and a retry with it is a replay on the engine.
 * The only re-routing is the database correcting a stale route: `place_bid`
 * answering `bid_on_engine`, or the engine answering that it does not hold the
 * sale. Neither placed anything.
 *
 * Never throws on a transport error — a failed request returns a structured
 * `{ success: false, reason: 'rpc_error' }` so callers render one path.
 */
export async function placeBid(
  supabase: AuctisyncSupabase,
  input: PlaceBidInput,
  transport: BidTransport = {},
): Promise<BidResult> {
  const doFetch = transport.fetch ?? withTimeout(DEFAULT_REQUEST_TIMEOUT_MS);
  for (let pass = 0; pass < 2; pass++) {
    const lookup = await getBidRoute(supabase, input.lotId, { fresh: pass > 0 });
    if (!lookup.ok) {
      // Nothing was sent to either book — say so, rather than "unconfirmed".
      return {
        success: false,
        reason: 'route_unavailable',
        message: 'Could not reach the auction. Your bid was not placed — please try again.',
      };
    }
    if (lookup.route.path === 'engine') {
      const result = await placeBidOnEngine(supabase, lookup.route, input, doFetch);
      if (result !== 'wrong_book') return result;
    } else {
      const result = await placeBidOnLedger(supabase, input);
      if (result.reason !== 'bid_on_engine') return result;
    }
    forgetBidRoute(supabase, input.lotId);
  }
  // The route moved twice in one bid — a sale being handed over this instant.
  return {
    success: false,
    reason: 'route_unavailable',
    message: 'Bidding on this lot is opening. Your bid was not placed — please try again.',
  };
}

/** The `place_bid` RPC — the ledger in Postgres. */
async function placeBidOnLedger(
  supabase: AuctisyncSupabase,
  input: PlaceBidInput,
): Promise<BidResult> {
  const { data, error } = await supabase.rpc('place_bid', {
    p_lot_id: input.lotId,
    p_amount_cents: input.amountCents,
    p_max_amount_cents: input.maxAmountCents ?? input.amountCents,
    p_idempotency_key: input.idempotencyKey,
    p_source: input.source ?? 'web',
  });

  if (error) {
    return { success: false, reason: 'rpc_error', message: error.message };
  }
  return data as unknown as BidResult;
}

/**
 * The engine's refusal names that differ from `place_bid`'s. Bidders get ONE
 * vocabulary whichever book took their bid — `describeBidFailure` speaks
 * `place_bid`'s — so the engine's few odd names are translated here, once,
 * for the website and the apps alike.
 */
const ENGINE_REASONS: Readonly<Record<string, string>> = {
  too_low: 'amount_too_low',
  closed: 'lot_not_open',
  auction_not_live: 'auction_not_active',
};

/** The words the engine uses for "the sale is not here". */
const NOT_HELD = 'auction_not_published';

/** The engine's two "who are you at this house?" refusals, both cured by registering. */
function needsRegistration(status: number, error: unknown): boolean {
  return status === 401 || (status === 403 && error === 'not_registered_with_house');
}

/** Ask without throwing; null for a body that is not JSON. */
async function readJson(res: Response): Promise<Record<string, unknown> | null> {
  try {
    const body: unknown = await res.json();
    return typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function num(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

/** The engine's 409 as `place_bid` would have said it. */
function fromEngineRefusal(body: Record<string, unknown>): BidResult {
  const raw = typeof body.rejection_reason === 'string' ? body.rejection_reason : 'rejected';
  const reason = ENGINE_REASONS[raw] ?? raw;
  const result: BidResult = { success: false, reason };
  const current = num(body.current_high_cents);
  if (current !== undefined) result.current_bid_cents = current;
  const minimum = num(body.minimum_acceptable_cents);
  if (reason === 'amount_too_low' && minimum !== undefined) result.required_cents = minimum;
  const ceiling = num(body.current_ceiling_cents);
  if (ceiling !== undefined) result.current_ceiling_cents = ceiling;
  if (raw === 'closed') result.status = 'closed';
  return result;
}

/** The engine's 200 as `place_bid` would have said it. */
function fromEngineAcceptance(body: Record<string, unknown>): BidResult {
  if (body.idempotent_replay === true) {
    const current = num(body.current_high_cents);
    return {
      success: true,
      idempotent_replay: true,
      ...(current !== undefined ? { current_bid_cents: current } : {}),
    };
  }
  const result: BidResult = { success: true };
  const price = num(body.price_cents);
  if (price !== undefined) result.current_bid_cents = price;
  const next = num(body.next_min_cents);
  if (next !== undefined) result.next_min_cents = next;
  if (typeof body.triggered_extension === 'boolean')
    result.triggered_extension = body.triggered_extension;
  if (typeof body.close_time === 'string') result.ends_at = body.close_time;
  return result;
}

/**
 * One bid on the engine. Registers the bidder with the house first when the
 * engine says it does not know them there (a first bid at a house — `place_bid`
 * did that as a side effect; the engine never does), refreshes the token so it
 * carries the house, and sends the SAME bid again. `wrong_book` when the
 * engine does not hold the sale and would not take it.
 */
async function placeBidOnEngine(
  supabase: AuctisyncSupabase,
  route: Extract<BidRoute, { path: 'engine' }>,
  input: PlaceBidInput,
  doFetch: typeof fetch,
): Promise<BidResult | 'wrong_book'> {
  const ceiling = input.maxAmountCents ?? input.amountCents;
  const body = JSON.stringify({
    auction_id: route.engineAuctionId,
    lot_id: input.lotId,
    bid_id: input.idempotencyKey,
    amount_cents: input.amountCents,
    max_proxy_cents: ceiling,
  });

  for (let attempt = 0; attempt < 2; attempt++) {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) return { success: false, reason: 'unauthenticated' };

    let res: Response;
    try {
      res = await doFetch(`${route.engineUrl}/v1/bid`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-House-Id': route.houseId,
        },
        body,
      });
    } catch (cause) {
      // Unreachable — or reached, with the answer lost. We cannot tell which,
      // so the bid is UNCONFIRMED, never failed, and the key is kept: a retry
      // with it is a replay on the engine. Never `place_bid` (one book).
      return {
        success: false,
        reason: 'rpc_error',
        message: cause instanceof Error ? cause.message : 'The auction did not respond.',
      };
    }

    const answer = await readJson(res);
    if (res.status === 200 && answer !== null) return fromEngineAcceptance(answer);
    if (res.status === 409 && answer !== null) return fromEngineRefusal(answer);
    if (res.status === 404 && answer?.error === NOT_HELD) return 'wrong_book';
    // A valid sign-in that may not bid here: the house's own owner or staff
    // (Jim, 2026-10-07 — they do not bid in their own sales). Never a reason
    // to re-register: that loop is what told an owner to "sign in first".
    if (res.status === 403 && answer?.error === 'role_not_allowed') {
      return {
        success: false,
        reason: answer.engine_role === 'tenant_admin' ? 'house_staff' : 'role_not_allowed',
      };
    }

    if (attempt === 0 && needsRegistration(res.status, answer?.error)) {
      const { data: reg, error: regError } = await supabase.rpc('register_with_house', {
        p_house_id: route.houseId,
      });
      if (regError) return { success: false, reason: 'rpc_error', message: regError.message };
      const registered = reg as { ok?: boolean; reason?: string } | null;
      if (registered?.ok !== true) {
        return {
          success: false,
          reason: registered?.reason === 'unauthenticated' ? 'unauthenticated' : 'lot_not_found',
        };
      }
      // A new token, carrying the house.
      const { error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) return { success: false, reason: 'unauthenticated' };
      continue;
    }
    if (needsRegistration(res.status, answer?.error))
      return { success: false, reason: 'unauthenticated' };

    if (res.status >= 500) {
      // The engine is up and did NOT record the bid (it says so, and says to
      // retry with the same id) — or a gateway in front of it failed. Either
      // way the bidder hears "not confirmed", the key is kept, and a retry is
      // a replay. Worded so `isReachabilityFailure` reads it as such.
      return {
        success: false,
        reason: 'rpc_error',
        message: 'The auction did not respond. Check your connection and try again.',
      };
    }
    return {
      success: false,
      reason: 'rpc_error',
      message: `The auction refused the request (${res.status}).`,
    };
  }
  return { success: false, reason: 'unauthenticated' };
}

/**
 * A fresh idempotency key.
 *
 * The key exists so a retry over flaky mobile data cannot place a SECOND bid:
 * `place_bid` recognises a repeated key and returns the original outcome rather
 * than bidding again. It therefore has to be stable across retries of one
 * attempt and different between attempts — which is a uniqueness requirement,
 * not a secrecy one, so a non-cryptographic fallback is acceptable where
 * `randomUUID` is unavailable (Hermes does not always provide it).
 */
export function newIdempotencyKey(): string {
  const cryptoRef = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (typeof cryptoRef?.randomUUID === 'function') return cryptoRef.randomUUID();
  // Time gives ordering and coarse uniqueness; the random suffix separates two
  // attempts inside the same millisecond.
  return `bid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Turns a `place_bid` rejection into a sentence for the bidder.
 *
 * SHARED ON PURPOSE. The website had this inline in `PublicLotDetail`. A bidder
 * who is told one thing on their phone and another on the site — about why
 * their money did not go through, during a live auction — has every reason to
 * distrust both. One wording.
 *
 * `message` wins when present: the server said something specific, and our
 * generic mapping should not talk over it.
 */
/**
 * How a bid message should READ to the person receiving it.
 *
 * `error` means something went wrong and the bid did not happen.
 * `notice` means nothing went wrong — the bid was simply unnecessary, and the
 * bidder's position is intact.
 *
 * The distinction lives HERE, next to the wording, rather than in each client.
 * Every surface rendered every bid message in red, so "your maximum is already
 * in" — the friendliest thing the system can say — looked identical to a
 * declined card. Leaving the classification to the UI is what let that happen
 * on the phone and the website at once.
 */
export type BidMessageTone = 'notice' | 'error';

export interface BidMessage {
  tone: BidMessageTone;
  text: string;
}

export function describeBidFailure(result: BidResult): BidMessage {
  if (result.message) return { tone: 'error', text: result.message };
  switch (result.reason) {
    case 'unauthenticated':
      return { tone: 'error', text: 'Please sign in first.' };
    case 'house_staff':
      // The engine (role_not_allowed for a house's tenant_admin) and
      // place_bid both say this — the same sentence on either book.
      return { tone: 'error', text: "You help run this house, so you can't bid in its sales." };
    case 'bid_on_engine':
      // Only a client that predates the engine reaches this: today's client
      // re-routes before the bidder sees it.
      return {
        tone: 'error',
        text: 'This auction has moved to faster bidding. Please refresh the page or update the app.',
      };
    case 'lot_not_found':
      return { tone: 'error', text: 'Lot not found.' };
    case 'lot_not_open':
      return {
        tone: 'error',
        text: `Lot is ${result.status ?? 'not open'} and not accepting bids.`,
      };
    case 'auction_closed':
      return { tone: 'error', text: 'Auction is closed.' };
    case 'auction_not_active':
      return { tone: 'error', text: `Auction is ${result.status ?? 'not active'}.` };
    case 'amount_too_low':
      return {
        tone: 'error',
        text: `Bid too low. Minimum is ${
          result.required_cents !== undefined
            ? formatCents(result.required_cents)
            : 'a higher amount'
        }.`,
      };
    case 'ceiling_not_raised':
      // The server-side twin of `explainPointlessBid`, which catches this
      // BEFORE the request. Same situation, so the same sentence AND the same
      // tone: a bidder whose maximum is already recorded has not made a
      // mistake, and it should not matter to them which side noticed.
      return {
        tone: 'notice',
        text:
          result.current_ceiling_cents !== undefined
            ? `Your maximum of ${formatCents(
                result.current_ceiling_cents,
              )} is already in. To raise it, enter more than that.`
            : 'Your maximum on this lot is already higher than that. To raise it, enter a larger amount.',
      };
    case 'buyer_not_approved':
      return { tone: 'error', text: 'Your bidder account is not approved for this auction.' };
    case 'preauth_missing':
      return {
        tone: 'error',
        text: 'Add a card on file to finish activating your approval, then try again.',
      };
    default:
      return {
        tone: 'error',
        text: result.reason ? `Bid rejected: ${result.reason}.` : 'Bid rejected.',
      };
  }
}
